import { useState, useEffect, useRef } from 'react';
import { usersAPI } from '@/services/api';

// 타입 정의
export type EmailStatusType = 'idle' | 'checking' | 'available' | 'unavailable';

export interface EmailState {
  status: EmailStatusType;
  isVerified: boolean;
}

export interface VerificationState {
  code: string;
  setCode: (code: string) => void;
  isSending: boolean;
  isVerifying: boolean;
  countdown: number;
}

export interface VerificationActions {
  sendCode: () => Promise<void>;
  verifyCode: () => Promise<void>;
}

export interface UseEmailVerificationReturn {
  email: EmailState;
  verification: VerificationState;
  actions: VerificationActions;
  formatCountdown: (seconds: number) => string;
}

export const useEmailVerification = (emailAddress: string): UseEmailVerificationReturn => {
  const [emailStatus, setEmailStatus] = useState<EmailStatusType>('idle');
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isVerifyingCode, setIsVerifyingCode] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [countdown, setCountdown] = useState(0);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!emailAddress || !emailAddress.includes('@')) {
      setEmailStatus('idle');
      setIsEmailVerified(false);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailAddress)) {
      setEmailStatus('idle');
      setIsEmailVerified(false);
      return;
    }

    const timer = setTimeout(async () => {
      setEmailStatus('checking');
      try {
        const result = await usersAPI.checkEmail(emailAddress);
        if (result.available) {
          setEmailStatus('available');
        } else {
          setEmailStatus('unavailable');
          setIsEmailVerified(false);
        }
      } catch (_error: unknown) {
        setEmailStatus('idle');
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [emailAddress]);

  useEffect(() => {
    if (countdown > 0) {
      countdownIntervalRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            if (countdownIntervalRef.current) {
              clearInterval(countdownIntervalRef.current);
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    }

    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, [countdown]);

  const sendVerificationCode = async () => {
    if (!emailAddress || emailStatus !== 'available') {
      return;
    }

    setIsSendingCode(true);
    try {
      const result = await usersAPI.sendVerificationCode(emailAddress);
      setCountdown(600);
      if (result.email_sent === false) {
        setIsEmailVerified(true);
        alert('⚠️ 현재 시스템에 문제가 있어서 이메일 발송에 실패했습니다. 이메일 인증 없이 진행할 수 있습니다.');
      } else {
        alert('인증 코드가 이메일로 발송되었습니다.');
      }
    } catch (_error: unknown) {
      setIsEmailVerified(true);
      alert('⚠️ 현재 시스템에 문제가 있어서 이메일 발송에 실패했습니다. 이메일 인증 없이 진행할 수 있습니다.');
    } finally {
      setIsSendingCode(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      alert('6자리 인증 코드를 입력해주세요.');
      return;
    }

    setIsVerifyingCode(true);
    try {
      const result = await usersAPI.verifyEmailCode(emailAddress, verificationCode);
      if (result.verified) {
        setIsEmailVerified(true);
        setCountdown(0);
        alert('이메일 인증이 완료되었습니다.');
      } else {
        alert(result.message || '인증 코드가 올바르지 않습니다.');
      }
    } catch (error: any) {
      alert(error.response?.data?.detail || '인증 코드 검증에 실패했습니다.');
    } finally {
      setIsVerifyingCode(false);
    }
  };

  const formatCountdown = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return {
    email: {
      status: emailStatus,
      isVerified: isEmailVerified,
    },
    verification: {
      code: verificationCode,
      setCode: setVerificationCode,
      isSending: isSendingCode,
      isVerifying: isVerifyingCode,
      countdown,
    },
    actions: {
      sendCode: sendVerificationCode,
      verifyCode: handleVerifyCode,
    },
    formatCountdown,
  };
};

